<script setup>
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import db from "../main.js";
import { ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const props = defineProps({
  user: String,
});

const collections = ref([]);

// 各イベントの詳細を取得し、完了次第すぐに表示
const fetchEventDetails = async (eventId, uniqueEvents, processedEventIds) => {
  if (processedEventIds.has(eventId)) return;
  processedEventIds.add(eventId);

  try {
    const eventDocRef = doc(db, "Goods_products", eventId);
    const eventDocSnap = await getDoc(eventDocRef);

    if (eventDocSnap.exists()) {
      const eventData = eventDocSnap.data();
      const eventInfo = uniqueEvents.get(eventId);
      const eventTitle = eventData.eventtitle;
      const eventImage = eventData.vendor_image;
      console.log("Event Image:", eventData.vendor_image);
      // 詳細情報で更新
      uniqueEvents.set(eventId, {
        ...eventInfo,
        eventImage,
        eventTitle,
        loading: false, // ローディング完了
      });

      // 取得完了次第すぐにコレクションを更新して表示
      collections.value = Array.from(uniqueEvents.values());
    }
  } catch (error) {
    console.error(`イベント詳細の取得に失敗しました (eventId: ${eventId}):`, error);
    // エラー時もローディング状態を解除
    const eventInfo = uniqueEvents.get(eventId);
    uniqueEvents.set(eventId, {
      ...eventInfo,
      loading: false,
    });
    collections.value = Array.from(uniqueEvents.values());
  }
};

const getCollections = async () => {
  // 購入済みのイベント商品を取得（Goods_productordersサブコレクションから）
  const querySnapshot = await getDocs(
    collection(db, "users", props.user.uid, "Goods_productorders")
  );

  const uniqueEvents = new Map();
  const processedEventIds = new Set(); // 処理済みのeventIdを追跡

  // 各ドキュメント（{orderID}）を処理
  for (const docSnapshot of querySnapshot.docs) {
    const orderId = docSnapshot.id; // ドキュメントID = orderId
    console.log("Order ID:", orderId);

    // Goods_productorders/{orderId}ドキュメントからevent_idを取得
    try {
      const orderDocRef = doc(db, "Goods_productorders", orderId);
      const orderDocSnap = await getDoc(orderDocRef);

      if (orderDocSnap.exists()) {
        console.log("Document data:", orderDocSnap.data());
        const orderData = orderDocSnap.data();
        const eventId = orderData.event_id;
        const vendorname_ja = orderData.vendorname_ja;

        console.log("Event ID:", eventId);
        console.log("Vendor Name JA:", vendorname_ja);

        if (eventId && vendorname_ja) {
          // 同じイベントは重複させない（eventIdをキーとして使用）
          if (!uniqueEvents.has(eventId)) {
            // まず基本情報で表示（ローディング状態）
            uniqueEvents.set(eventId, {
              vendorname_ja,
              eventImage: "/img/lp_example1.png", // デフォルト画像
              eventTitle: `${vendorname_ja}限定グッズ販売`, // デフォルトタイトル
              eventId: eventId,
              loading: true, // ローディング状態を追加
            });

            // リアルタイムでコレクションを更新
            collections.value = Array.from(uniqueEvents.values());

            // この時点で即座にイベント詳細を取得開始
            fetchEventDetails(eventId, uniqueEvents, processedEventIds);
          }
        }
      } else {
        // docSnap.data() will be undefined in this case
        console.log("No such document!");
      }
    } catch (error) {
      console.error(`Order document取得に失敗しました (orderId: ${orderId}):`, error);
    }
  }
};

getCollections();

const toEventDetail = (eventId) => {
  router.push(`/mycollectiondetail/${eventId}`);
};

const toLinkto = (url) => {
  router.push(url);
};
</script>

<template>
  <v-app>
    <!-- メインコンテンツ -->
    <div class="main-container">
      <div class="title-section">
        <h2 class="page-title">マイコレクション</h2>
        <p class="page-description">購入済みのイベントを選択</p>
      </div>

      <!-- 購入済みイベント一覧 -->
      <div v-if="collections.length > 0" class="collections-grid">
        <div
          v-for="(collection, index) in collections"
          :key="index"
          class="collection-card"
          @click="toEventDetail(collection.eventId)"
        >
          <div class="event-image-container">
            <img :src="collection.eventImage" :alt="collection.eventTitle" class="event-image" />
            <div v-if="collection.loading" class="loading-overlay">
              <div class="loading-spinner"></div>
            </div>
          </div>
          <div class="event-title">
            {{ collection.eventTitle }}
            <div v-if="collection.loading" class="loading-text">読み込み中...</div>
          </div>
        </div>
      </div>

      <!-- 購入履歴がない場合のメッセージ -->
      <div v-else class="no-collections-message">
        <p>購入履歴はありません。</p>
      </div>

      <!-- 戻るボタン -->
      <div class="return-container">
        <button class="return-btn" @click="toLinkto('/myaccount')">戻る</button>
      </div>
    </div>
  </v-app>
</template>

<style scoped>
/* メインコンテンツ */
.main-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  background-color: white;
  min-height: 60vh;
}

.title-section {
  text-align: center;
  margin-bottom: 40px;
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  color: #000;
  margin: 0 0 10px 0;
}

.page-description {
  font-size: 16px;
  color: #666;
  margin: 0;
}

/* コレクショングリッド */
.collections-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
}

.collection-card {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.collection-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.event-image-container {
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.event-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.event-title {
  padding: 20px;
  font-size: 18px;
  font-weight: bold;
  color: #000;
  text-align: center;
  background-color: #f8f9fa;
}

/* 戻るボタン */
.return-container {
  text-align: center;
  margin-top: 40px;
}

.return-btn {
  padding: 15px 20px !important;
  font-size: 0.9rem !important;
  border-radius: 100px !important;
  border: none !important;
  margin: 0 5px !important;
  background: rgb(226, 226, 226) !important;
  color: rgb(142, 144, 147) !important;
  box-shadow: none !important;
  cursor: pointer;
}

.return-btn:active,
.return-btn:hover {
  background: rgb(190, 190, 190) !important;
  color: rgb(255, 255, 255) !important;
}

/* ローディング表示 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 12px;
  color: #666;
  margin-top: 5px;
}

.event-image-container {
  position: relative;
}

/* 購入履歴がない場合のメッセージ */
.no-collections-message {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.no-collections-message p {
  font-size: 18px;
  margin: 0;
}

/* レスポンシブデザイン */
@media (max-width: 768px) {
  .collections-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}
</style>
